import { useCallback, useEffect, useRef, useState } from "react";

// AudioContext condiviso per il rilevamento del parlato (VAD).
let sharedAudioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!sharedAudioCtx) sharedAudioCtx = new AC();
  if (sharedAudioCtx.state === "suspended") sharedAudioCtx.resume().catch(() => {});
  return sharedAudioCtx;
}

// Operator-side WebRTC manager — MESH multi-partecipante (fino a 4 per stanza).
//
// Ogni soccorso (event_id) = una stanza di segnalazione:
//   wss://.../api/ws/{event_id}?role=operator
// Il server relaya messaggi mirati fra i peer della stanza. Ruoli possibili:
//   caller (naufrago), operator (Centrale), rescuer (motovedetta).
//
// Messaggi dal server:
//   {type:"self", id, role}                -> il nostro peer_id
//   {type:"peers", peers:[{id,role}]}      -> peer già presenti: offriamo a ognuno
//   {type:"peer-joined", id, role}         -> nuovo peer: attendiamo la sua offerta
//   {type:"peer-left", id}                 -> chiudiamo quella connessione
//   {type:"offer"|"answer"|"candidate", from, ...}
// Segnalazione mirata: ogni offer/answer/candidate include `to:<peerId>`; il
// server aggiunge `from:<mittente>`. Manteniamo UNA RTCPeerConnection per peer.
//
// Public call shape: { eventId, nome, cognome, zone_name, number,
//   status:"connecting"|"in-call", muted, micAvailable, startedAt, speaking }

const RECONNECT_DELAY_MS = 1500;

export function useOperatorCalls({ apiOrigin, enabled = false, iceServers, callEvents = [], onCallEnded, onDismiss }) {
  const [callStates, setCallStates] = useState({});
  const callStatesRef = useRef({});
  callStatesRef.current = callStates;

  const sessionsRef = useRef(new Map());   // eventId -> session
  const micStreamRef = useRef(null);

  const apiOriginRef = useRef(apiOrigin); apiOriginRef.current = apiOrigin;
  const iceServersRef = useRef(iceServers); iceServersRef.current = iceServers;
  const enabledRef = useRef(enabled); enabledRef.current = enabled;
  const onCallEndedRef = useRef(onCallEnded); onCallEndedRef.current = onCallEnded;
  const onDismissRef = useRef(onDismiss); onDismissRef.current = onDismiss;
  const callEventsRef = useRef(callEvents); callEventsRef.current = callEvents;

  const patch = useCallback((id, fields) => {
    setCallStates((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], ...fields } } : prev));
  }, []);

  const wsOpen = (s) => s && s.ws && s.ws.readyState === WebSocket.OPEN;

  const maybeStopMic = () => {
    if (sessionsRef.current.size === 0 && micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
  };

  const updateStatus = useCallback((session) => {
    let connected = false, caller = false, rescuer = false;
    for (const p of session.peers.values()) {
      if (p.pc && p.pc.connectionState === "connected") connected = true;
      if (p.role === "caller") caller = true;
      if (p.role === "rescuer") rescuer = true;
    }
    patch(session.eventId, {
      status: connected ? "in-call" : "connecting",
      startedAt: session.callStart,
      callerPresent: caller, rescuerPresent: rescuer,
      callerMuted: !!session.roleMuted.caller,
      rescuerMuted: !!session.roleMuted.rescuer,
      micMuted: !!session.micMuted,
    });
  }, [patch]);

  const closePeer = useCallback((session, peerId) => {
    const p = session.peers.get(peerId);
    if (!p) return;
    try { p.pc.close(); } catch (e) {}
    try { p.localTrack && p.localTrack.stop(); } catch (e) {}
    try { if (p.audioEl) p.audioEl.srcObject = null; } catch (e) {}
    session.peers.delete(peerId);
  }, []);

  const endSession = useCallback((eventId, dismiss = false) => {
    const s = sessionsRef.current.get(eventId);
    if (s) {
      clearTimeout(s.reconnectTimer);
      if (s.ws) { try { s.ws.onclose = null; s.ws.close(); } catch (e) {} }
      for (const peerId of Array.from(s.peers.keys())) closePeer(s, peerId);
      if (s.callStart && onCallEndedRef.current) {
        const ended = Date.now();
        const meta = {
          event_id: eventId,
          event_number: s.meta.number,
          event_year: s.meta.year,
          zone_name: s.meta.zone_name,
          operator_username: s.meta.operator_username,
          nome: s.meta.nome,
          cognome: s.meta.cognome,
          started_at: new Date(s.callStart).toISOString(),
          ended_at: new Date(ended).toISOString(),
          duration_sec: Math.max(0, Math.round((ended - s.callStart) / 1000)),
        };
        const rec = s.recorder;
        if (rec && rec.state !== "inactive") {
          rec.onstop = () => {
            let blob = null;
            try {
              if (s.recChunks && s.recChunks.length) blob = new Blob(s.recChunks, { type: s.recMime || "audio/webm" });
            } catch (e) { console.debug("rec blob", e); }
            onCallEndedRef.current(meta, blob);
          };
          try { rec.stop(); } catch (e) { onCallEndedRef.current(meta, null); }
        } else {
          onCallEndedRef.current(meta, null);
        }
      }
      sessionsRef.current.delete(eventId);
      maybeStopMic();
    }
    setCallStates((prev) => { const n = { ...prev }; delete n[eventId]; return n; });
    if (dismiss && onDismissRef.current) onDismissRef.current(eventId);
  }, [closePeer]);

  // Crea una RTCPeerConnection verso un peer remoto della stanza.
  const setupPeer = useCallback((session, peerId, role) => {
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    const audioEl = new Audio();
    audioEl.autoplay = true;
    const peer = { pc, role: role || session.roles.get(peerId), audioEl, analyser: null, vadBuf: null, speakUntil: 0, localTrack: null, pending: [] };
    session.peers.set(peerId, peer);

    pc.onicecandidate = (e) => {
      if (e.candidate && wsOpen(session)) {
        session.ws.send(JSON.stringify({ type: "candidate", to: peerId, candidate: e.candidate }));
      }
    };
    pc.ontrack = (e) => {
      audioEl.srcObject = e.streams[0];
      audioEl.muted = !!session.roleMuted[peer.role];
      audioEl.play().catch(() => {});
      try {
        const ac = getAudioCtx();
        if (ac) {
          const srcNode = ac.createMediaStreamSource(e.streams[0]);
          const an = ac.createAnalyser();
          an.fftSize = 512;
          srcNode.connect(an);
          peer.analyser = an;
          peer.vadBuf = new Uint8Array(an.fftSize);
          // Registrazione: aggancia lo stream GREZZO (prima del mute UI) al mix.
          if (session.recDest) { try { srcNode.connect(session.recDest); } catch (err2) {} }
        }
      } catch (err) {}
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected" && !session.callStart) session.callStart = Date.now();
      if (st === "failed" || st === "closed") closePeer(session, peerId);
      updateStatus(session);
    };
    // Aggiungi il nostro microfono (clone per ogni peer) o solo ricezione.
    if (micStreamRef.current) {
      const base = micStreamRef.current.getAudioTracks()[0];
      if (base) {
        const t = base.clone();
        t.enabled = !session.micMuted;
        peer.localTrack = t;
        pc.addTrack(t, micStreamRef.current);
      }
    }
    if (!peer.localTrack) {
      try { pc.addTransceiver("audio", { direction: "recvonly" }); } catch (e) {}
    }
    return peer;
  }, [closePeer, updateStatus]);

  const makeOffer = useCallback(async (session, peerId, role) => {
    const peer = setupPeer(session, peerId, role);
    try {
      const offer = await peer.pc.createOffer();
      await peer.pc.setLocalDescription(offer);
      if (wsOpen(session)) {
        session.ws.send(JSON.stringify({ type: "offer", to: peerId, sdp: peer.pc.localDescription }));
      }
    } catch (e) { closePeer(session, peerId); }
  }, [setupPeer, closePeer]);

  const handleOffer = useCallback(async (session, from, rawSdp) => {
    const desc = rawSdp && rawSdp.sdp ? rawSdp : { type: "offer", sdp: rawSdp };
    let peer = session.peers.get(from);
    if (!peer) peer = setupPeer(session, from, session.roles.get(from));
    try {
      await peer.pc.setRemoteDescription(desc);
      for (const c of peer.pending) { try { await peer.pc.addIceCandidate(c); } catch (e) {} }
      peer.pending = [];
      const ans = await peer.pc.createAnswer();
      await peer.pc.setLocalDescription(ans);
      if (wsOpen(session)) session.ws.send(JSON.stringify({ type: "answer", to: from, sdp: peer.pc.localDescription }));
    } catch (e) { closePeer(session, from); }
  }, [setupPeer, closePeer]);

  const connect = useCallback((session) => {
    const wsUrl = `${apiOriginRef.current.replace(/^http/, "ws")}/api/ws/${session.eventId}?role=operator`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch (e) { return; }
    session.ws = ws;

    ws.onmessage = async (evt) => {
      let msg; try { msg = JSON.parse(evt.data); } catch (e) { return; }
      switch (msg.type) {
        case "self":
          session.selfId = msg.id;
          break;
        case "peers":
          (msg.peers || []).forEach((p) => {
            session.roles.set(p.id, p.role);
            // Siamo l'ultimo entrato: iniziamo noi l'offerta verso i presenti.
            if (!session.peers.has(p.id)) makeOffer(session, p.id, p.role);
          });
          break;
        case "peer-joined":
          session.roles.set(msg.id, msg.role); // attendiamo la sua offerta
          break;
        case "peer-left":
          closePeer(session, msg.id);
          updateStatus(session);
          break;
        case "offer":
          if (msg.from) await handleOffer(session, msg.from, msg.sdp);
          break;
        case "answer": {
          const peer = msg.from && session.peers.get(msg.from);
          if (peer) {
            const desc = msg.sdp && msg.sdp.sdp ? msg.sdp : { type: "answer", sdp: msg.sdp };
            try { await peer.pc.setRemoteDescription(desc); } catch (e) {}
          }
          break;
        }
        case "candidate":
        case "ice": {
          const peer = msg.from && session.peers.get(msg.from);
          const cand = msg.candidate;
          if (!peer || !cand) break;
          if (peer.pc.remoteDescription && peer.pc.remoteDescription.type) {
            try { await peer.pc.addIceCandidate(cand); } catch (e) {}
          } else {
            peer.pending.push(cand);
          }
          break;
        }
        default:
          break;
      }
    };

    ws.onclose = () => {
      // Riconnessione finché il soccorso è attivo (la chiamata NON deve cadere).
      if (!sessionsRef.current.has(session.eventId)) return;
      if (!enabledRef.current) return;
      const stillActive = callEventsRef.current.some((e) => e.id === session.eventId);
      if (!stillActive) return;
      clearTimeout(session.reconnectTimer);
      session.reconnectTimer = setTimeout(() => {
        if (sessionsRef.current.has(session.eventId)) connect(session);
      }, RECONNECT_DELAY_MS);
    };
    ws.onerror = () => {};
  }, [makeOffer, handleOffer, closePeer, updateStatus]);

  const answer = useCallback(async (eventId) => {
    if (sessionsRef.current.has(eventId)) return;
    const ev = callEventsRef.current.find((e) => e.id === eventId) || callStatesRef.current[eventId] || {};
    const meta = {
      nome: ev.nome, cognome: ev.cognome, number: ev.number, year: ev.year,
      zone_name: ev.zone_name, operator_username: ev.operator_username,
    };
    const session = {
      eventId, meta, ws: null, selfId: null,
      peers: new Map(), roles: new Map(),
      roleMuted: {}, micMuted: false, callStart: null, reconnectTimer: null,
    };
    sessionsRef.current.set(eventId, session);
    setCallStates((prev) => ({
      ...prev,
      [eventId]: {
        eventId, nome: meta.nome, cognome: meta.cognome, zone_name: meta.zone_name,
        number: meta.number, status: "connecting", micAvailable: true, startedAt: null,
        speaking: false, callerSpeaking: false, rescuerSpeaking: false,
        callerPresent: false, rescuerPresent: false,
        callerMuted: false, rescuerMuted: false, micMuted: false,
      },
    }));
    let micOk = true;
    if (!micStreamRef.current) {
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false }).catch(() => null);
    }
    if (!micStreamRef.current) micOk = false;
    patch(eventId, { micAvailable: micOk });
    // --- registrazione chiamata: mix di operatore + tutti i peer remoti ---
    // Si registra dagli stream grezzi (indipendente dal mute UI): un
    // partecipante mutato ma che parla viene comunque registrato.
    try {
      const ac = getAudioCtx();
      if (ac && typeof MediaRecorder !== "undefined") {
        session.recDest = ac.createMediaStreamDestination();
        if (micStreamRef.current) {
          try {
            session.recMicNode = ac.createMediaStreamSource(micStreamRef.current);
            session.recMicNode.connect(session.recDest);
          } catch (e) { console.debug("rec mic", e); }
        }
        const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]
          .find((m) => { try { return MediaRecorder.isTypeSupported(m); } catch (e) { return false; } });
        const recorder = mime
          ? new MediaRecorder(session.recDest.stream, { mimeType: mime })
          : new MediaRecorder(session.recDest.stream);
        session.recChunks = [];
        session.recMime = recorder.mimeType || mime || "audio/webm";
        recorder.ondataavailable = (e) => { if (e.data && e.data.size) session.recChunks.push(e.data); };
        session.recorder = recorder;
        recorder.start(1000);
      }
    } catch (e) { console.debug("rec setup", e); }
    connect(session);
  }, [patch, connect]);

  const reject = useCallback((eventId) => {
    endSession(eventId, true);
  }, [endSession]);

  const hangup = useCallback((eventId) => {
    endSession(eventId, true);
  }, [endSession]);

  // Mute selettivo per RUOLO: silenzia l'audio che l'operatore SENTE da quel ruolo.
  const setMuteRole = useCallback((eventId, role, muted) => {
    const s = sessionsRef.current.get(eventId);
    if (s) {
      s.roleMuted[role] = muted;
      for (const p of s.peers.values()) {
        if (p.role === role && p.audioEl) p.audioEl.muted = muted;
      }
    }
    patch(eventId, role === "rescuer" ? { rescuerMuted: muted } : { callerMuted: muted });
  }, [patch]);

  // Muta il microfono dell'operatore verso tutti i peer.
  const toggleMic = useCallback((eventId) => {
    const s = sessionsRef.current.get(eventId);
    const next = !(callStatesRef.current[eventId]?.micMuted);
    if (s) {
      s.micMuted = next;
      for (const p of s.peers.values()) {
        if (p.localTrack) p.localTrack.enabled = !next;
      }
    }
    patch(eventId, { micMuted: next });
  }, [patch]);

  // Chiudi le sessioni il cui soccorso è sparito (terminato).
  const idsKey = callEvents.map((e) => e.id).join(",");
  useEffect(() => {
    if (!enabledRef.current) {
      for (const id of Array.from(sessionsRef.current.keys())) endSession(id);
      setCallStates({});
      return;
    }
    const active = new Set(callEvents.map((e) => e.id));
    for (const id of Array.from(sessionsRef.current.keys())) {
      if (!active.has(id)) endSession(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, enabled]);

  useEffect(() => () => {
    for (const id of Array.from(sessionsRef.current.keys())) endSession(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // VAD: la card lampeggia solo quando il naufrago (caller) parla davvero.
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      for (const [eid, s] of sessionsRef.current) {
        const st = callStatesRef.current[eid];
        if (!st || st.status !== "in-call") continue;
        const now = Date.now();
        const roleSpeak = { caller: false, rescuer: false };
        for (const p of s.peers.values()) {
          if (!p.analyser || !p.vadBuf) continue;
          if (s.roleMuted[p.role]) continue;
          p.analyser.getByteTimeDomainData(p.vadBuf);
          let sum = 0;
          for (let i = 0; i < p.vadBuf.length; i++) { const d = (p.vadBuf[i] - 128) / 128; sum += d * d; }
          const rms = Math.sqrt(sum / p.vadBuf.length);
          if (rms > 0.045) p.speakUntil = now + 350;
          if (now < (p.speakUntil || 0)) {
            if (p.role === "rescuer") roleSpeak.rescuer = true; else roleSpeak.caller = true;
          }
        }
        const upd = {};
        if (!!st.callerSpeaking !== roleSpeak.caller) upd.callerSpeaking = roleSpeak.caller;
        if (!!st.rescuerSpeaking !== roleSpeak.rescuer) upd.rescuerSpeaking = roleSpeak.rescuer;
        if (!!st.speaking !== roleSpeak.caller) upd.speaking = roleSpeak.caller;
        if (Object.keys(upd).length) patch(eid, upd);
      }
    }, 150);
    return () => clearInterval(id);
  }, [enabled, patch]);

  const calls = Object.values(callStates);
  return { calls, answer, reject, hangup, setMuteRole, toggleMic };
}
